'use client'

import React, { useEffect, useId, useRef, useState } from 'react'
import { X } from 'lucide-react'
import {
  type StatsCalculationMethod,
  type VisaCategoryStat,
} from './visaCategoryStats'

interface StatsCalculationPopoverProps {
  category: VisaCategoryStat
  calculationMethod: StatsCalculationMethod
}

export function StatsCalculationPopover({
  category,
  calculationMethod,
}: StatsCalculationPopoverProps) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const panelId = useId()
  const showNational = category.showNationalComparison

  useEffect(() => {
    if (!open) return

    const handlePointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false)
      }
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }

    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [open])

  return (
    <div ref={rootRef} className="relative inline-flex items-baseline">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((value) => !value)}
        className="text-xs md:text-sm text-white/60 underline decoration-white/25 underline-offset-2 hover:text-white/85 transition-colors"
      >
        About our data
      </button>

      {open && (
        <div
          id={panelId}
          role="dialog"
          aria-labelledby={`${panelId}-title`}
          className="absolute bottom-full left-0 z-50 mb-1 w-[min(24rem,calc(100vw-3rem))] overflow-hidden rounded-lg border border-gray-200 bg-stone p-4 text-left shadow-xl whitespace-normal break-words"
        >
          <div className="flex items-start justify-between gap-3 mb-4">
            <p id={`${panelId}-title`} className="text-base font-medium text-primary">
              About our data
            </p>
            <button
              type="button"
              aria-label="Close"
              onClick={() => setOpen(false)}
              className="shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <dl className="mb-4 grid gap-3 sm:grid-cols-2">
            <div className="rounded-md border border-gray-200 px-3 py-2.5">
              <dt className="text-sm font-medium leading-snug text-gray-700">
                Cases filed
                <br />

              </dt>
              <dd className="mt-1 text-3xl font-semibold tabular-nums text-primary">
                {category.casesFiled.toLocaleString()}
              </dd>
              <span className='text-xs text-gray-500'>
                (all time)
              </span>
            </div>
            <div className="rounded-md border border-gray-200 px-3 py-2.5">
              <dt className="text-sm font-medium leading-snug text-gray-700">
                Approval rate
                <br />

              </dt>
              <dd className="mt-1 text-3xl font-semibold tabular-nums text-primary">
                {category.casesApproved} / {category.casesDecided}
              </dd>
              <span className='text-xs text-gray-500'>
                (current administration)
              </span>
            </div>
          </dl>

          <ul className="min-w-0 space-y-2 text-sm text-gray-600 leading-relaxed [overflow-wrap:anywhere]">
            <li>
              <span className="font-medium text-primary">Formula:</span>{' '}
              {calculationMethod.rateFormula}
            </li>
            {showNational && category.nationalFiscalYear ? (
              <li>
                <span className="font-medium text-primary">National benchmark:</span>{' '}
                {category.nationalFiscalYear}
                {category.nationalAgency && ` · ${category.nationalAgency}`}
                {category.nationalPrimaryDataset && (
                  <span className="block mt-1">{category.nationalPrimaryDataset}</span>
                )}
              </li>
            ) : (
              category.nationalComparisonNote && (
                <li>
                  <span className="font-medium text-primary">National benchmark:</span>{' '}
                  {category.nationalComparisonNote}
                </li>
              )
            )}
          </ul>
        </div>
      )}
    </div>
  )
}
